// FURNITURE options menu — a small floating panel anchored at a clicked piece's projected screen
// centre (above the 3D canvas). Lists the piece's FUNCTIONAL option (Sleep for a bed, Open for a
// safebox — see shared FURNITURE_FUNCTIONS) plus Move / Pick up for players entitled to edit it.
// Clicking anywhere outside the panel closes it.

export interface FurnitureMenuOption {
  label: string;
  onPick: () => void;
}

const STYLE_ID = "ghostland-furnimenu-style";
const CSS = `
#gl-furnimenu {
  position: fixed; z-index: 40; display: none; flex-direction: column; min-width: 120px;
  transform: translate(-50%, -50%);
  background: rgba(0,0,0,.92); border: 2px solid #f4f4f4; image-rendering: pixelated;
  font-family: 'Silkscreen', ui-monospace, monospace; padding: 6px;
}
#gl-furnimenu.open { display: flex; }
#gl-furnimenu h1 {
  margin: 0 0 6px; padding: 2px 4px 4px; font-size: 9px; letter-spacing: 1px;
  color: #f4f4f4; border-bottom: 1px solid #2a2a2a; text-align: center;
}
#gl-furnimenu button {
  background: #000; border: 2px solid #6a6a6a; color: #b0b0b0;
  margin-top: 4px; padding: 7px 12px; cursor: pointer; font-size: 9px; letter-spacing: 1px;
  font-family: inherit;
}
#gl-furnimenu button:first-of-type { margin-top: 0; }
#gl-furnimenu button:hover { background: #f4f4f4; color: #000; border-color: #f4f4f4; }
`;

let panel: HTMLDivElement | null = null;

function ensure(): void {
  if (!document.getElementById(STYLE_ID)) {
    const s = document.createElement("style"); s.id = STYLE_ID; s.textContent = CSS; document.head.appendChild(s);
  }
  if (panel) return;
  panel = document.createElement("div");
  panel.id = "gl-furnimenu";
  document.body.appendChild(panel);
  // Any press outside the panel dismisses it (capture phase, so it beats the canvas handlers).
  window.addEventListener("pointerdown", (e) => {
    if (isFurnitureMenuOpen() && !panel!.contains(e.target as Node)) closeFurnitureMenu();
  }, { capture: true });
}

/** Open the menu centred on the piece's screen point (clamped into the viewport). */
export function openFurnitureMenu(x: number, y: number, title: string, options: FurnitureMenuOption[]): void {
  if (!options.length) return;
  ensure();
  panel!.replaceChildren();
  const h = document.createElement("h1");
  h.textContent = title;
  panel!.appendChild(h);
  for (const opt of options) {
    const b = document.createElement("button");
    b.textContent = opt.label;
    b.addEventListener("click", () => { closeFurnitureMenu(); opt.onPick(); });
    panel!.appendChild(b);
  }
  panel!.classList.add("open");
  // Clamp the centre point so the panel never hangs off-screen.
  const r = panel!.getBoundingClientRect();
  const cx = Math.max(r.width / 2 + 8, Math.min(window.innerWidth - r.width / 2 - 8, x));
  const cy = Math.max(r.height / 2 + 8, Math.min(window.innerHeight - r.height / 2 - 8, y));
  panel!.style.left = `${cx}px`;
  panel!.style.top = `${cy}px`;
}

export function closeFurnitureMenu(): void {
  panel?.classList.remove("open");
}

export function isFurnitureMenuOpen(): boolean {
  return panel?.classList.contains("open") ?? false;
}
